import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';

// Import components
import WellnessImageGallery from './components/WellnessImageGallery';
import EESCenterOverview from './components/EESCenterOverview';
import WellnessCenterTabs from './components/WellnessCenterTabs';
import BookingCalendar from './components/BookingCalendar';
import ContactForm from './components/ContactForm';

const EESCenterDetails = () => {
  const [searchParams] = useSearchParams();
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('facilities');
  const [showContactForm, setShowContactForm] = useState(false);

  const centerId = searchParams.get('id');

  // Mock EES center data
  const mockCenter = {
    id: 1,
    title: "Serenity Wellness & EES Center",
    address: "123 Healing Way, Sedona, AZ 86336",
    eesUnits: 24,
    capacity: 12,
    rating: 4.9,
    reviewsCount: 247,
    yearsExperience: 8,
    completedSessions: "3.2k",
    pricing: {
      singleSession: 150,
      dayPass: 300,
      weeklyPackage: 1200,
      monthlyUnlimited: 3800
    },
    images: [
      "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop"
    ],
    host: {
      name: "Dr. Maya Chen",
      phone: "(555) 123-4567",
      email: "maya@serenitywellness.com",
      avatar: "https://randomuser.me/api/portraits/women/45.jpg",
      rating: 4.9,
      reviewsCount: 186,
      bio: "Dr. Maya Chen is a certified wellness practitioner with over 15 years of experience in holistic healing and energy medicine. She specializes in EES technology integration and personalized wellness protocols."
    },
    certifications: [
      "Certified EES Practitioner",
      "Holistic Health Certification",
      "Energy Medicine Specialist",
      "Wellness Center Licensed"
    ],
    badges: [
      "Verified Host",
      "Top Rated",
      "Instant Book",
      "Medical Grade EES",
      "24/7 Support"
    ],
    facilities: [
      "24 EES Scalar Energy Units",
      "Climate Controlled Chambers",
      "Private Consultation Rooms",
      "Meditation Garden",
      "Wellness Library",
      "Organic Juice Bar",
      "Relaxation Lounges",
      "Grounding Areas",
      "Sound Healing Room",
      "Crystal Therapy Space",
      "Yoga Studio",
      "Himalayan Salt Room"
    ],
    wellnessPrograms: [
      {
        name: "EES Intensive Healing",
        price: "$1,200",
        duration: "3 days",
        groupSize: "1-4 guests",
        frequency: "Flexible scheduling",
        description: "Deep cellular regeneration program with extended EES sessions, energy healing, and personalized wellness protocols."
      },
      {
        name: "Detox & Rejuvenation Retreat",
        price: "$2,800",
        duration: "7 days",
        groupSize: "6-12 guests",
        frequency: "Monthly",
        description: "Comprehensive detoxification program combining EES technology with nutritional therapy, meditation, and bodywork."
      },
      {
        name: "Stress Relief & Energy Balance",
        price: "$450",
        duration: "1 day",
        groupSize: "1-6 guests",
        frequency: "Daily availability",
        description: "Perfect for busy professionals seeking stress relief and energy restoration through EES sessions and relaxation techniques."
      }
    ],
    nearbyPlaces: [
      { name: "Red Rock State Park", distance: "2.5 miles" },
      { name: "Tlaquepaque Arts Village", distance: "1.2 miles" },
      { name: "Chapel of the Holy Cross", distance: "3.1 miles" },
      { name: "Whole Foods Market", distance: "0.8 miles" },
      { name: "Sedona Airport Vortex", distance: "4.2 miles" },
      { name: "Oak Creek Trail", distance: "1.7 miles" }
    ],
    reviews: [
      {
        guestName: "Sarah Johnson",
        rating: 5,
        date: "2 weeks ago",
        comment: "Absolutely life-changing experience! The EES sessions helped me feel more energized and balanced than I have in years. Dr. Maya's guidance was invaluable.",
        experience: ["Energy Boost", "Pain Relief", "Better Sleep"]
      },
      {
        guestName: "Michael Rodriguez",
        rating: 5,
        date: "1 month ago",
        comment: "Incredible facility with state-of-the-art EES technology. The staff is knowledgeable and the environment is so peaceful. I've noticed significant improvements in my overall well-being.",
        experience: ["Stress Relief", "Mental Clarity", "Emotional Balance"]
      },
      {
        guestName: "Emma Thompson",
        rating: 4,
        date: "6 weeks ago",
        comment: "Great experience overall. The EES sessions were relaxing and I felt a noticeable difference in my energy levels. The facility is clean and well-maintained.",
        experience: ["Relaxation", "Energy Enhancement", "Wellness"]
      }
    ],
    virtualTour: "https://example.com/virtual-tour",
    video: "https://example.com/center-video"
  };

  useEffect(() => {
    const fetchCenter = async () => {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        setCenter(mockCenter);
        setIsSaved(false);
        setLoading(false);
      }, 1000);
    };

    if (centerId) {
      fetchCenter();
    }
  }, [centerId]);

  const handleSave = () => {
    setIsSaved(!isSaved);
    // In real app, sync with backend
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: center?.title,
        text: `Check out this EES wellness center: ${center?.title}`,
        url: window.location.href
      });
    } else {
      // Fallback to copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleBooking = (bookingData) => {
    console.log('Booking data:', bookingData);
    // In real app, process booking
  };

  const getBreadcrumbs = () => {
    const breadcrumbs = [
      { label: 'Home', path: '/homepage' },
      { label: 'Wellness Centers', path: '/wellness-center-listings' },
      { label: center?.title || 'EES Center Details', path: null }
    ];
    return breadcrumbs;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Loading skeleton */}
            <div className="space-y-8">
              <div className="h-8 bg-secondary-100 rounded w-2/3 skeleton"></div>
              <div className="h-64 sm:h-80 lg:h-96 bg-secondary-100 rounded-lg skeleton"></div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="h-64 bg-secondary-100 rounded-lg skeleton"></div>
                  <div className="h-96 bg-secondary-100 rounded-lg skeleton"></div>
                </div>
                <div className="space-y-6">
                  <div className="h-96 bg-secondary-100 rounded-lg skeleton"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!center) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <Icon name="Heart" size={64} className="text-accent mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-text-primary mb-2">
                EES Center Not Found
              </h1>
              <p className="text-text-secondary mb-6">
                The wellness center you're looking for doesn't exist or has been removed.
              </p>
              <Link
                to="/wellness-center-listings"
                className="inline-flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-md hover:bg-primary-700 transition-all duration-200"
              >
                <Icon name="ArrowLeft" size={16} />
                <span>Back to Centers</span>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16 lg:pt-18">
        {/* Breadcrumb */}
        <div className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center space-x-2 text-sm">
              {getBreadcrumbs().map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <Icon name="ChevronRight" size={14} className="text-text-secondary" />
                  )}
                  {crumb.path ? (
                    <Link
                      to={crumb.path}
                      className="text-text-secondary hover:text-text-primary transition-colors duration-200"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-text-primary font-medium truncate">{crumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>
        </div>

        {/* Mobile Actions Bar */}
        <div className="lg:hidden bg-surface border-b border-border sticky top-16 z-10">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSave}
                className={`p-2 rounded-md transition-all duration-200 ${
                  isSaved 
                    ? 'bg-error text-white' :'bg-secondary-100 text-text-secondary hover:bg-error hover:text-white'
                }`}
              >
                <Icon name="Heart" size={18} fill={isSaved ? "currentColor" : "none"} />
              </button>
              <button
                onClick={handleShare}
                className="p-2 bg-secondary-100 text-text-secondary rounded-md hover:bg-secondary-200 transition-all duration-200"
              >
                <Icon name="Share" size={18} />
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowContactForm(true)}
                className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent-600 transition-all duration-200"
              >
                Contact Host
              </button>
              <button className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-all duration-200">
                Book Now
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery */}
              <WellnessImageGallery 
                images={center.images}
                title={center.title}
                virtualTour={center.virtualTour}
                video={center.video}
              />

              {/* Center Overview */}
              <EESCenterOverview 
                center={center}
                isSaved={isSaved}
                onSave={handleSave}
                onShare={handleShare}
                onContact={() => setShowContactForm(true)}
              />

              {/* Center Tabs */}
              <WellnessCenterTabs 
                center={center}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Booking Calendar */}
              <BookingCalendar 
                onBooking={handleBooking}
                pricing={center.pricing}
              />

              {/* Host Contact Card */}
              <div className="card p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <Image
                    src={center.host.avatar}
                    alt={center.host.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-text-primary">{center.host.name}</h3>
                    <div className="flex items-center space-x-1 mb-1">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={14}
                            className={i < Math.floor(center.host.rating) ? 'text-warning fill-current' : 'text-secondary-300'}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-text-secondary">
                        {center.host.rating} ({center.host.reviewsCount} reviews)
                      </span>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-text-secondary mb-4">
                  {center.host.bio}
                </p>
                
                <div className="space-y-3">
                  <button
                    onClick={() => setShowContactForm(true)}
                    className="w-full flex items-center justify-center space-x-2 bg-primary text-white py-3 rounded-md hover:bg-primary-700 transition-all duration-200"
                  >
                    <Icon name="MessageCircle" size={16} />
                    <span>Send Message</span>
                  </button>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button className="flex items-center justify-center space-x-2 bg-accent-100 text-accent-600 py-2 rounded-md hover:bg-accent hover:text-white transition-all duration-200">
                      <Icon name="Phone" size={16} />
                      <span className="text-sm">Call</span>
                    </button>
                    <button className="flex items-center justify-center space-x-2 bg-secondary-100 text-text-secondary py-2 rounded-md hover:bg-secondary-200 transition-all duration-200">
                      <Icon name="Calendar" size={16} />
                      <span className="text-sm">Schedule</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Contact Form Modal */}
      {showContactForm && (
        <ContactForm
          center={center}
          host={center.host}
          onClose={() => setShowContactForm(false)}
        />
      )}
    </div>
  );
};

export default EESCenterDetails;